import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Modal } from "react-bootstrap";
import axios from "axios";
import DataTableSearch from "../Common/HelpCommon/DataTableSearch";
import DataTablePagination from "../Common/HelpCommon/DataTablePagination";

const API_URL = process.env.REACT_APP_API;

const TransferAdjustmentHelp = ({
  onRecordClick,
  name,
  recordId,
  recordDisplay,
  tabIndexHelp,
  disabledField,
  firstInputRef,
  Company_Code: propCompanyCode,   // ← match the prop name you pass from parent
}) => {
  const CompanyCode = propCompanyCode ?? sessionStorage.getItem("Company_Code"); // ← ?? not ||

  const [showModal, setShowModal] = useState(false);
  const [popupContent, setPopupContent] = useState([]);
  const [enteredId, setEnteredId] = useState("");
  const [displayLabel, setDisplayLabel] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowIndex, setSelectedRowIndex] = useState(-1);
  const [apiDataFetched, setApiDataFetched] = useState(false);
  const itemsPerPage = 10;

  // ── Re-fetch when company changes ──────────────────────────────────────────
  useEffect(() => {
    setApiDataFetched(false);   // ← forces fetchData to run again
    setPopupContent([]);
  }, [propCompanyCode]);        // ← triggers on every company change

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!CompanyCode) return;   // ← guard: don't fetch if no company selected yet
    try {
      const res = await axios.get(
        `${API_URL}/getdata-for-TransferHelp?Company_Code=${CompanyCode}`
      );
      setPopupContent(res.data.all_data);
      setApiDataFetched(true);
    } catch (err) {
      console.error("Error fetching transfer help data:", err);
    }
  }, [CompanyCode]);

  useEffect(() => {
    if (!apiDataFetched) fetchData();
  }, [apiDataFetched, fetchData]);

  // ── Open modal ─────────────────────────────────────────────────────────────
  const fetchAndOpenPopup = useCallback(async () => {
    if (!apiDataFetched) await fetchData();
    setShowModal(true);
  }, [apiDataFetched, fetchData]);

  // ── Sync from parent props ─────────────────────────────────────────────────
  useMemo(() => {
    if (recordId !== undefined && recordId !== enteredId) {
      setEnteredId(recordId ?? "");
      setDisplayLabel(recordDisplay ?? "");
    }
  }, [recordId, recordDisplay]);

  // ── ID field change ────────────────────────────────────────────────────────
  const handleCodeChange = (e) => {
    const value = e.target ? e.target.value : e;
    setEnteredId(value);

    if (!value) {
      setDisplayLabel("");
      if (onRecordClick) onRecordClick(null);
      return;
    }

    const match = popupContent.find((r) => String(r.Id) === String(value));
    if (match) {
      applyRecord(match);
    } else {
      setDisplayLabel("");
    }
  };

  // ── Tab key ────────────────────────────────────────────────────────────────
  const handleKeyDown = async (e) => {
    if (e.key === "Tab" && e.target.id === name) {
      if (!apiDataFetched) await fetchData();
      const match = popupContent.find((r) => String(r.Id) === String(enteredId));
      if (match) applyRecord(match);
    }
  };

  // ── Apply selected record ──────────────────────────────────────────────────
  const applyRecord = (item) => {
    setEnteredId(item.Id);
    setDisplayLabel(
      `${item.Company_Name_E} | To: ${item.ToACName} | From: ${item.FromACName}`
    );
    if (onRecordClick) onRecordClick(item);
    setShowModal(false);
  };

  // ── Double-click row ───────────────────────────────────────────────────────
  const handleRecordDoubleClick = (item) => applyRecord(item);

  // ── Search / pagination ────────────────────────────────────────────────────
  const filteredData = popupContent.filter((item) => {
    const s = searchTerm.toLowerCase();
    return (
      String(item.Id).includes(s) ||
      (item.Company_Name_E || "").toLowerCase().includes(s) ||
      (item.ToACName || "").toLowerCase().includes(s) ||
      (item.FromACName || "").toLowerCase().includes(s) ||
      (item.CrossCompanyName || "").toLowerCase().includes(s) ||
      (item.CrossToACName || "").toLowerCase().includes(s) ||
      (item.CrossFromAcc || "").toLowerCase().includes(s)
    );
  });

  const startIndex = (currentPage - 1) * itemsPerPage;
  const itemsToDisplay = filteredData.slice(startIndex, startIndex + itemsPerPage);

  // ── F1 key ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const onF1 = (e) => {
      if (e.key === "F1" && e.target.id === name) {
        e.preventDefault();
        setSearchTerm(e.target.value);
        fetchAndOpenPopup();
      }
    };
    window.addEventListener("keydown", onF1);
    return () => window.removeEventListener("keydown", onF1);
  }, [name, fetchAndOpenPopup]);

  // ── Arrow / Enter navigation ───────────────────────────────────────────────
  useEffect(() => {
    const onNav = (e) => {
      if (!showModal) return;
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedRowIndex((p) => Math.max(p - 1, 0));
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedRowIndex((p) => Math.min(p + 1, itemsToDisplay.length - 1));
      } else if (e.key === "Enter" && selectedRowIndex >= 0) {
        e.preventDefault();
        handleRecordDoubleClick(itemsToDisplay[selectedRowIndex]);
      } else if (e.key === "Escape") {
        setShowModal(false);
      }
    };
    window.addEventListener("keydown", onNav);
    return () => window.removeEventListener("keydown", onNav);
  }, [showModal, selectedRowIndex, itemsToDisplay]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="d-flex flex-row">
      <div className="d-flex align-items-center">
        <input
          type="text"
          className="form-control ms-2"
          id={name}
          autoComplete="off"
          value={enteredId}
          onChange={handleCodeChange}
          onKeyDown={handleKeyDown}
          style={{ width: "90px", height: "35px" }}
          tabIndex={tabIndexHelp}
          disabled={disabledField}
          ref={firstInputRef}
        />
        <Button
          variant="primary"
          onClick={fetchAndOpenPopup}
          className="ms-1"
          style={{ width: "30px", height: "35px" }}
          disabled={disabledField}
        >
          ...
        </Button>
        <label
          className="form-labels ms-2"
          style={{ whiteSpace: "nowrap", fontSize: "14px", fontWeight: "bold", marginTop: "5px" }}
        >
          {displayLabel}
        </label>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} dialogClassName="custom-modal-xl">
        <Modal.Header style={{ display: "flex", justifyContent: "center", position: "relative" }}>
          <Modal.Title>Transfer Adjustment Help</Modal.Title>
          <Button
            style={{ background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer", color: "blue", position: "absolute", right: "10px" }}
            onClick={() => setShowModal(false)}
          >
            X
          </Button>
        </Modal.Header>

        <DataTableSearch data={popupContent} onSearch={(v) => { setSearchTerm(v); setCurrentPage(1); }} />

        <Modal.Body>
          <div className="table-responsive">
            <table className="custom-small-table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Company</th>
                  <th>To A/C</th>
                  <th>From A/C</th>
                  <th>Cross Company</th>
                  <th>Cross To A/C</th>
                  <th>Cross From A/C</th>
                </tr>
              </thead>
              <tbody>
                {itemsToDisplay.map((item, index) => (
                  <tr
                    key={item.Id}
                    style={{
                      cursor: "pointer",
                      backgroundColor: selectedRowIndex === index ? "#d6e9f9" : "white",
                    }}
                    onDoubleClick={() => handleRecordDoubleClick(item)}
                    onClick={() => setSelectedRowIndex(index)}
                  >
                    <td>{item.Id}</td>
                    <td>{item.Company_Name_E}</td>
                    <td>{item.ToACName}</td>
                    <td>{item.FromACName}</td>
                    <td>{item.CrossCompanyName}</td>
                    <td>{item.CrossToACName}</td>
                    <td>{item.CrossFromAcc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <DataTablePagination
            totalItems={filteredData.length}
            itemsPerPage={itemsPerPage}
            onPageChange={(p) => { setCurrentPage(p); setSelectedRowIndex(-1); }}
          />
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TransferAdjustmentHelp;